/**
 * Word-level redlines for a proposed fix: a before/after passage read as
 * phrases, so a rewritten sentence shows "old sentence → new sentence"
 * instead of alternating struck and added words. Pure and small-input only
 * (an LCS table over tokens); Inspector's edits are capped at 6,000 chars.
 *
 * @module components/regulation/finding/changes/wordDiff
 */

export interface DiffSegment {
  readonly kind: "same" | "add" | "del";
  readonly text: string;
}

/** A word-level diff (LCS over words and whitespace), small inputs only. */
export function diffWords(before: string, after: string): DiffSegment[] {
  // Words, whitespace and punctuation are separate tokens, so "residence and"
  // → "residence," reads as one deleted word and one added comma.
  const tokenize = (text: string) => (text ? text.split(/(\s+|[,.;:()])/).filter((token) => token !== "") : []);
  const left = tokenize(before);
  const right = tokenize(after);
  const rows = left.length;
  const cols = right.length;
  const table: number[][] = Array.from({ length: rows + 1 }, () => Array<number>(cols + 1).fill(0));
  for (let i = rows - 1; i >= 0; i--) {
    for (let j = cols - 1; j >= 0; j--) {
      table[i]![j] = left[i] === right[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const segments: DiffSegment[] = [];
  const push = (kind: DiffSegment["kind"], text: string) => {
    const last = segments.at(-1);
    if (last && last.kind === kind) segments[segments.length - 1] = { kind, text: last.text + text };
    else segments.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (left[i] === right[j]) {
      push("same", left[i]!);
      i++;
      j++;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      push("del", left[i]!);
      i++;
    } else {
      push("add", right[j]!);
      j++;
    }
  }
  while (i < rows) push("del", left[i++]!);
  while (j < cols) push("add", right[j++]!);
  return segments;
}

/** PDF text wraps mid-sentence: a line break before a lowercase word or "(" is a space. */
export function joinWrappedLines(text: string): string {
  return text.replace(/[ \t]*\n[ \t]*(?=[a-z(])/g, " ");
}

/**
 * The word diff read as phrases: a run of changes with at most two unchanged
 * words between them becomes one deletion followed by one insertion, so a
 * rewritten sentence reads "old sentence → new sentence" instead of
 * alternating struck and added words.
 */
export function diffPhrases(before: string, after: string): DiffSegment[] {
  const segments = diffWords(joinWrappedLines(before), joinWrappedLines(after));
  const out: DiffSegment[] = [];
  let del = "";
  let add = "";
  let open = false;
  const flush = () => {
    if (del) out.push({ kind: "del", text: del });
    if (add) out.push({ kind: "add", text: add });
    del = "";
    add = "";
    open = false;
  };
  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index]!;
    if (segment.kind === "del") {
      del += segment.text;
      open = true;
    } else if (segment.kind === "add") {
      add += segment.text;
      open = true;
    } else {
      const words = segment.text.split(/\s+/).filter((word) => /\w/.test(word)).length;
      const more = segments.slice(index + 1).some((next) => next.kind !== "same");
      if (open && more && words <= 2 && segments[index + 1]?.kind !== "same") {
        del += segment.text;
        add += segment.text;
      } else {
        flush();
        out.push(segment);
      }
    }
  }
  flush();
  return out;
}
